  <template>
    
    <div class="container py-3">
      <!-- Page Header -->
      <div class="page-header">
        <h2>{{ $t('product.title') }}</h2>
        <button class="btn btn-primary" @click="openAddModal">{{ $t('product.add') }}</button>
      </div>

      <!-- Search -->
      <div class="search-wrapper mb-3">
        <span class="search-icon">🔍</span>
        <input v-model="search" type="text" class="form-control" :placeholder="$t('product.searchPlaceholder')" />
      </div>

      <!-- Products Table -->
      <div class="table-card">
        <table class="premium-table table">
          <thead>
            <tr>
              <th>{{ $t('common.name') }}</th>
              <th>{{ $t('product.sku') }}</th>
              <th>{{ $t('product.category') }}</th>
              <th>{{ $t('product.warehouse') }}</th>
              <th>{{ $t('product.stock') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredProducts.length === 0">
              <td colspan="6">
                <div class="empty-state">
                  <div class="empty-icon">📦</div>
                  <p>No products found</p>
                </div>
              </td>
            </tr>
            <tr v-for="product in filteredProducts" :key="product.id">
              <td><strong>{{ product.name }}</strong></td>
              <td><code style="background:var(--bs-secondary-bg, #f1f5f9);padding:2px 6px;border-radius:4px;font-size:0.78rem;">{{ product.sku }}</code></td>
              <td>{{ product.category_name }}</td>
              <td>{{ product.warehouse_name }}</td>
              <td>
                <span class="qty-badge"
                  :class="stockMap[product.id] > 10 ? 'qty-ok' : stockMap[product.id] > 0 ? 'qty-low' : 'qty-zero'">
                  {{ stockMap[product.id] ?? 0 }}
                </span>
              </td>
              <td>
                <button class="btn-action btn-action-warning" @click="openEditModal(product)">✏️ {{ $t('common.edit') }}</button>
                <button class="btn-action btn-action-danger" @click="deleteProduct(product.id)">🗑️ {{ $t('common.delete') }}</button>
                <button class="btn-action btn-action-info" @click="openAdjustStockModal(product)">⚖️ {{ $t('product.adjustStock') }}</button>
                <button class="btn-action btn-action-secondary" @click="openReconcileModal(product)">🔄 {{ $t('product.reconcile') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <MainPagination
        v-model:currentPage="currentPage"
        v-model:pageSize="pageSize"
        :total="totalRecords"
      />

      <!-- Add/Edit Product Modal -->
      <div class="modal fade" ref="productModal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <form @submit.prevent="saveProduct">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? $t('product.edit') : $t('product.add') }}</h5>
                <button type="button" class="btn-close" @click="hideProductModal"></button>
              </div>
              <div class="modal-body">
                <div v-if="errorMessage" class="alert alert-danger py-2 mb-3">
                  {{ errorMessage }}
                </div>
                <div class="mb-3">
                  <label class="form-label">{{ $t('common.name') }}</label>
                  <input v-model="form.name" type="text" class="form-control" required />
                </div>
                <div class="mb-3">
                  <label class="form-label">{{ $t('product.sku') }}</label>
                  <input v-model="form.sku" type="text" class="form-control" required />
                </div>
                <input type="hidden" v-model="form.base_uom" />
                <div class="mb-3">
                  <label class="form-label">{{ $t('product.category') }}</label>
                  <select v-model="form.category" class="form-select" required>
                    <option disabled value="">{{ $t('product.selectCategory') }}</option>
                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                      {{ cat.name }}
                    </option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">{{ $t('product.warehouse') }}</label>
                  <select v-model="form.warehouse" class="form-select" required>
                    <option disabled value="">{{ $t('product.selectWarehouse') }}</option>
                    <option v-for="wh in warehouses" :key="wh.id" :value="wh.id">
                      {{ wh.name }}
                    </option>
                  </select>
                </div>
                <div class="mb-3">
                  <label class="form-label">{{ $t('product.quantity') }}</label>
                  <input v-model.number="form.quantity" type="number" class="form-control" />
                  <div class="form-text text-muted">Leave as 0 to add stock later via Stock Adjustments or Purchase Orders.</div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="hideProductModal">{{ $t('common.cancel') }}</button>
                <button type="submit" class="btn btn-primary">{{ isEditing ? $t('common.update') : $t('common.add') }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Adjust Stock Modal -->
      <div class="modal fade" ref="adjustStockModal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <form @submit.prevent="adjustStock">
              <div class="modal-header">
                <h5 class="modal-title">{{ $t('product.adjustStockFor') }} {{ stockProduct?.name }}</h5>
                <button type="button" class="btn-close" @click="hideAdjustStockModal"></button>
              </div>
              <div class="modal-body">
                <div class="mb-3">
                  <label class="form-label">{{ $t('product.adjustmentQty') }}</label>
                  <input v-model.number="stockForm.adjustment_quantity" type="number" class="form-control" required />
                </div>
                <div class="mb-3">
                  <label class="form-label">{{ $t('product.reason') }}</label>
                  <input v-model="stockForm.reason" type="text" class="form-control" required />
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="hideAdjustStockModal">{{ $t('common.cancel') }}</button>
                <button type="submit" class="btn btn-info">{{ $t('product.adjust') }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Reconcile Stock Modal -->
      <div class="modal fade" ref="reconcileModal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <form @submit.prevent="reconcileStock">
              <div class="modal-header">
                <h5 class="modal-title">{{ $t('product.reconcileFor') }} {{ reconcileProduct?.name }}</h5>
                <button type="button" class="btn-close" @click="hideReconcileModal"></button>
              </div>
              <div class="modal-body">
                <div v-if="reconcileResult" class="alert" :class="reconcileResult.adjustment === 0 ? 'alert-info' : (reconcileResult.adjustment > 0 ? 'alert-success' : 'alert-warning')">
                  <strong>{{ $t('product.reconcileResult') }}:</strong>
                  {{ $t('product.reconcilePrev') }}: {{ reconcileResult.previous_balance }} →
                  {{ $t('product.reconcileNew') }}: {{ reconcileResult.new_balance }}
                  ({{ reconcileResult.adjustment > 0 ? '+' : '' }}{{ reconcileResult.adjustment }})
                </div>
                <div class="mb-3">
                  <label class="form-label">{{ $t('product.physicalCount') }}</label>
                  <input v-model.number="reconcileForm.physical_count" type="number" min="0" class="form-control" required />
                  <div class="form-text text-muted">{{ $t('product.currentStock') }}: {{ stockMap[reconcileProduct?.id] ?? 0 }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label">{{ $t('product.reason') }}</label>
                  <select v-model="reconcileForm.reason" class="form-select" required>
                    <option value="STOCKTAKE">Stocktake</option>
                    <option value="DAMAGED_GOODS">Damaged Goods</option>
                    <option value="THEFT">Theft / Shrinkage</option>
                    <option value="DATA_ENTRY_ERROR">Data Entry Error</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="hideReconcileModal">{{ $t('common.cancel') }}</button>
                <button type="submit" class="btn btn-secondary">{{ $t('product.reconcile') }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>

    </div>
  </template>

  <script setup>
  import { ref, computed, watch, onMounted } from 'vue'
  import api from '../api/axios'
  import { adjustStock as apiAdjustStock, reconcileProduct as apiReconcile } from '../api/axios'
  import * as bootstrap from 'bootstrap'
  import { useI18n } from 'vue-i18n'
  import MainPagination from '../components/MainPagination.vue'

  const { t } = useI18n()


  const products = ref([])
  const search = ref('')
  const currentPage = ref(1)
  const pageSize = ref(10)
  const totalRecords = ref(0)
  const form = ref({})
  const isEditing = ref(false)
  const errorMessage = ref('')
  const stockForm = ref({})
  const stockProduct = ref(null)

  const categories = ref([])
  const warehouses = ref([])

  const stockMap = ref({})

  const fetchStock = async () => {
    const { data } = await api.get('stock/summary/')
    stockMap.value = (data || []).reduce((m, r) => {   // ← guard
      m[r.product_id] = r.quantity
      return m
    }, {})
  }

  const fetchDropdownData = async () => {
    try {
      const [catRes, whRes] = await Promise.all([
        api.get('categories/?pagination=false'),
        api.get('warehouses/?pagination=false'),
      ])
      categories.value = catRes.data
      warehouses.value = whRes.data
    } catch (err) {
      console.error('Error fetching dropdown data:', err)
    }
  }

  // Template refs
  const productModal = ref(null)
  const adjustStockModal = ref(null)

  let productModalInstance = null
  let adjustStockModalInstance = null

  const reconcileModal = ref(null)
  let reconcileModalInstance = null
  const reconcileProduct = ref(null)
  const reconcileForm = ref({ physical_count: 0, reason: 'STOCKTAKE' })
  const reconcileResult = ref(null)

  const filteredProducts = computed(() => {
    return products.value
  })

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products/', {
        params: {
          page: currentPage.value,
          page_size: pageSize.value,
          search: search.value
        }
      })
      products.value = res.data.results
      totalRecords.value = res.data.count
      console.log(products)
    } catch (err) {
      console.error(err)
    }
  }

  let searchTimeout = null
  watch(search, () => {
    currentPage.value = 1
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      fetchProducts()
    }, 300)
  })

  watch([currentPage, pageSize], () => {
    fetchProducts()
  })

  const baseUom = ref(null)   // will hold {id, code}

  const fetchBaseUoM = async () => {
    const { data } = await api.get('/uoms/?is_base=true')  // filter server side
    baseUom.value = data[0]   // assume one base unit per company
  }

  onMounted(() => {
    fetchProducts()
    fetchDropdownData()
    fetchStock()
    productModalInstance = new bootstrap.Modal(productModal.value)
    adjustStockModalInstance = new bootstrap.Modal(adjustStockModal.value)
    reconcileModalInstance = new bootstrap.Modal(reconcileModal.value)
  })

  // --- CRUD Functions ---
  const openAddModal = () => {
    isEditing.value = false
    errorMessage.value = ''
    fetchBaseUoM() 
    form.value = { name: '', sku: '', category: '', warehouse: '', quantity: 0, base_uom: baseUom.value?.id || null }
    productModalInstance.show()
  }

  const openEditModal = (product) => {
    isEditing.value = true
    errorMessage.value = ''
    form.value = { ...product }
    productModalInstance.show()
  }

  const hideProductModal = () => productModalInstance.hide()

  const saveProduct = async () => {
    errorMessage.value = ''
    try {
      if (isEditing.value) {
        await api.put(`/products/${form.value.id}/`, form.value)
      } else {
        await api.post('/products/', form.value)
      }
      fetchProducts()
      hideProductModal()
    } catch (err) {
      console.error(err)
      errorMessage.value = err.response?.data?.detail || err.response?.data?.sku?.[0] || err.response?.data?.name?.[0] || 'Failed to save product.'
    }
  }

  const deleteProduct = async (id) => {
    if (!confirm(t('product.confirmDelete'))) return
    try {
      await api.delete(`/products/${id}/`)
      fetchProducts()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.detail || 'Failed to deactivate product.')
    }
  }

  // --- Stock Adjustment ---
  const openAdjustStockModal = (product) => {
    stockProduct.value = product
    stockForm.value = { adjustment_quantity: 0, reason: '' }
    adjustStockModalInstance.show()
  }

  const hideAdjustStockModal = () => adjustStockModalInstance.hide()

const adjustStock = async () => {   // 2. local function keeps name
  try {
    await apiAdjustStock(stockProduct.value.id, {   // use renamed helper
      adjustment_quantity: stockForm.value.adjustment_quantity,
      reason: stockForm.value.reason
    })
    await fetchStock()        // 3. refresh readonly qty
    hideAdjustStockModal()
  } catch (e) {
    alert(e.response?.data?.detail || 'Adjustment failed')
  }
}

  const openReconcileModal = (product) => {
    reconcileProduct.value = product
    reconcileForm.value = { physical_count: stockMap.value[product.id] ?? 0, reason: 'STOCKTAKE' }
    reconcileResult.value = null
    reconcileModalInstance.show()
  }

  const hideReconcileModal = () => reconcileModalInstance.hide()

  const reconcileStock = async () => {
    try {
      const { data } = await apiReconcile(reconcileProduct.value.id, reconcileForm.value)
      reconcileResult.value = data
      if (data.adjustment !== 0) {
        await fetchStock()
        await fetchProducts()
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Reconciliation failed')
    }
  }
  </script>
